import { ArrowLeft, Check, Baby, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';

export type UserRole = 'mother' | 'father' | 'partner';

interface RoleStepProps {
  value: UserRole | null;
  onChange: (role: UserRole) => void;
  onNext: () => void;
  onBack: () => void;
}

export function RoleStep({ value, onChange, onNext, onBack }: RoleStepProps) {
  const { t } = useTranslation('onboarding');

  const roleOptions: { value: UserRole; label: string; description: string; icon: React.ReactNode }[] = [
    {
      value: 'mother',
      label: t('role.mother'),
      description: t('role.motherDesc'),
      icon: <Baby className="w-6 h-6" />,
    },
    {
      value: 'father',
      label: t('role.father'),
      description: t('role.fatherDesc'),
      icon: <User className="w-6 h-6" />,
    },
    {
      value: 'partner',
      label: t('role.partner'),
      description: t('role.partnerDesc'),
      icon: <Users className="w-6 h-6" />,
    },
  ];

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        <Users className="w-8 h-8 text-pp-orange" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">{t('role.title')}</h1>

      {/* Description */}
      <p className="text-white/70 mb-8 text-center">
        {t('role.description')}
      </p>

      {/* Role Selection */}
      <div className="space-y-3 mb-8">
        {roleOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all',
              value === option.value
                ? 'border-pp-orange bg-pp-orange/20'
                : 'border-white/20 bg-white/5 hover:border-white/30'
            )}
          >
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center',
                  value === option.value ? 'bg-pp-orange/30 text-pp-orange' : 'bg-white/10 text-white/50'
                )}
              >
                {option.icon}
              </div>
              <div className="text-left">
                <span
                  className={cn(
                    'font-semibold block',
                    value === option.value ? 'text-white' : 'text-white/90'
                  )}
                >
                  {option.label}
                </span>
                <span className="text-sm text-white/50">{option.description}</span>
              </div>
            </div>
            {value === option.value && (
              <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={onNext}
        disabled={!value}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t('common.next')}
      </button>
    </div>
  );
}
